import { Box, Text } from '@mantine/core'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import classes from '../LogVolunteeringActivityForm/LogVolunteeringActivityForm.module.scss'

import { AsyncSelect, FormField, Select } from '@percent/lemonade'
import { useCountryOptions } from '@percent/workplace-giving/common/hooks'
import { useOrgSearchCountries } from '@percent/workplace-giving/common/hooks/useOrgSearchCountries/useOrgSearchCountries'
import { useOrganisationSearchQuery } from '@percent/workplace-giving/common/hooks/useOrganisationSearchQuery/useOrganisationSearchQuery'
import { OrgSearchFor } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'
import { RequestNonprofitFooter } from '@percent/workplace-giving/app/RequestNonprofit/RequestNonprofitFooter/RequestNonprofitFooter'
import type { Organisation } from '@percent/workplace-giving/api/search/searchOrganisations/searchOrganisations.types'
import type { SelectOption } from '@percent/lemonade'

type NonprofitSearchSectionProps = Readonly<{
  countryValue: string
  organisationIdValue: string
  onCountryChange: (countryCode: string) => void
  onOrganisationChange: (orgId: string) => void
  countryTouched?: boolean
  countryError?: string
  organisationTouched?: boolean
  organisationError?: string
  defaultOrg?: Organisation
  defaultCountryCode: string
  disableCountryChange: boolean
  requireNonprofitFields: boolean | null
  searchOrgsWithEligibilityCheck: boolean | null
}>

export function NonprofitSearchSection({
  countryValue,
  organisationIdValue,
  onCountryChange,
  onOrganisationChange,
  countryTouched,
  countryError,
  organisationTouched,
  organisationError,
  defaultOrg,
  defaultCountryCode,
  disableCountryChange,
  requireNonprofitFields,
  searchOrgsWithEligibilityCheck
}: NonprofitSearchSectionProps) {
  const { t } = useTranslation()

  const { allowedCountryCodes } = useOrgSearchCountries(OrgSearchFor.Volunteering)

  const alpha3CountryCodes = useCountryOptions({
    countryCodes: allowedCountryCodes,
    includeWorldOption: true
  })

  const defaultCountryValue = useMemo(() => {
    if (defaultOrg?.countryCode) {
      return alpha3CountryCodes.find(a => a.value === defaultOrg.countryCode)
    }

    return alpha3CountryCodes.find(a => a.value === (countryValue || defaultCountryCode))
  }, [alpha3CountryCodes, defaultOrg?.countryCode, countryValue, defaultCountryCode])

  const [searchCountryCode, setSearchCountryCode] = useState<string>(defaultCountryValue?.value ?? defaultCountryCode)

  const {
    query: organisationQuery,
    setQuery: setOrganisationQuery,
    searchResults,
    isFetching,
    getOrganisationDescription
  } = useOrganisationSearchQuery({
    countryCode: searchCountryCode,
    searchOrgsWithEligibilityCheck,
    for: OrgSearchFor.Volunteering
  })

  const defaultOrganisationValue = useMemo<SelectOption | undefined>(
    () =>
      defaultOrg
        ? {
            value: defaultOrg.id,
            label: defaultOrg.name,
            description: getOrganisationDescription(defaultOrg)
          }
        : undefined,
    [defaultOrg, getOrganisationDescription]
  )

  const defaultValueForOrgField = useMemo<SelectOption | undefined>(() => {
    if (organisationIdValue !== defaultOrganisationValue?.value) {
      return searchResults.find(o => o.value === organisationIdValue)
    }

    return defaultOrganisationValue
  }, [defaultOrganisationValue, searchResults, organisationIdValue])

  const handleCountryChange = (countryCode: string) => {
    onCountryChange(countryCode)
    setSearchCountryCode(countryCode)
    setOrganisationQuery('')
    onOrganisationChange('')
  }

  return (
    <Box>
      <Text className={classes.SectionTitle}>{t('workplace_giving.volunteering.activityForm.supportedNonprofit')}</Text>

      <FormField
        necessity={requireNonprofitFields ? 'required' : 'optional'}
        label={t('workplace_giving.volunteering.activityForm.nonprofitCountry.label')}
        description={t('workplace_giving.wizard.nonprofitCountry.description')}
        status={countryTouched && countryError ? 'danger' : 'default'}
        statusMessage={countryError}
        data-testid="organisationCountryField"
        disabled={disableCountryChange}
      >
        <Select
          status={countryError ? 'danger' : 'default'}
          placeholder={t('workplace_giving.wizard.nonprofitCountry.placeholder')}
          options={alpha3CountryCodes}
          defaultValue={defaultCountryValue}
          onChange={event => handleCountryChange(event.value)}
          searchable
          showPrefixForSelectedOption
          borderRadiiSize="large"
        />
      </FormField>

      <FormField
        necessity={requireNonprofitFields ? 'required' : 'optional'}
        label={t('workplace_giving.volunteering.activityForm.findNonprofit.label')}
        status={organisationTouched && organisationError && !organisationQuery ? 'danger' : 'default'}
        statusMessage={organisationError}
        data-testid="organisationSearchField"
      >
        <AsyncSelect
          status={organisationError && !organisationQuery ? 'danger' : 'default'}
          defaultValue={defaultValueForOrgField}
          name="organisationId"
          key={searchCountryCode}
          placeholder={t('workplace_giving.wizard.selectNonprofit.placeholder')}
          onChange={e => onOrganisationChange(e?.value ?? '')}
          options={searchResults}
          setQuery={setOrganisationQuery}
          footer={
            <RequestNonprofitFooter
              searchText={organisationQuery}
              countryCode={searchCountryCode}
              eventOrigin="Log off-platform activity from"
            />
          }
          query={organisationQuery}
          loading={isFetching}
          loadingText={t('workplace_giving.common.searching')}
          noResultsFoundText={
            organisationQuery
              ? t('workplace_giving.common.noResultsFound')
              : t('workplace_giving.validation.emptyNonprofitNameQuery')
          }
          data-testid="organisationSearch"
          borderRadiiSize="large"
        />
      </FormField>
    </Box>
  )
}
